import { ISchemaVer, SCHEMA_VER, TRecChain, TRecDeviceDef, DeviceDef, IModuleDef, Chain, SuperModule } from "../model"
import { IArch } from "../arch"
import { IMidiNote } from "./midi"
import { DateTime} from '../time'
import { Identity } from "./id"

export type TRecSession = Record<string,ISession>

// AKA Snapshot
export interface ISession {
    meta: {
        modified: string
        accessed: string
        schema: ISchemaVer
        created: string
        immutable: boolean
        locked?: boolean
        midi?: IMidiNote
    }
    id: string
    name: string
    created: string
    chains: TRecChain
    devices: TRecDeviceDef
}

/**
 * Current loaded session
 */
export interface IActiveSession {
    name: string
    id: string
    modified: string
    dirty: boolean
}

export class Session {
    static create ({name}:{name:string}) : ISession {
        const now = DateTime.now()
        return {
            id: Identity.create(),
            name,
            created: now,
            chains: [],
            devices: {},
            meta: {
                schema: SCHEMA_VER,
                created: now,
                modified: now,
                accessed: now,
                immutable: false,
                locked: false,
            },
        }
    }

    static mutable (self: ISession) : boolean {
        return !self.meta.immutable
            && !self.meta.locked
    }

    /**
     * Create a copy of a session with new IDs for all devices and nodes
     * Optionally copies chains with proper module ID remapping
     * @param template - Source session to copy from
     * @param name - Name for the new session
     * @param importChains - Whether to copy chains (with module remapping)
     * @param arch - Architecture for module validation during chain remapping
     * @returns New session with fresh IDs
     */
    static copy ({template, name, importChains = false, arch}: {
        template: ISession
        name: string
        importChains?: boolean
        arch: IArch
    }): ISession {
        // Create base session with fresh metadata
        const session = Session.create({ name })

        // Build ID mapping: oldNodeId -> newNodeId for chain remapping
        const nodeIdMap: Record<string, string> = {}
        const newNodeById: Record<string, IModuleDef> = {}

        // Copy devices with new IDs
        for (const oldDevice of Object.values(template.devices)) {
            // Use DeviceDef.copy which handles node copying internally
            const newDevice = DeviceDef.copy({
                self: oldDevice,
                idgen: Identity.create,
                name: oldDevice.name
            })

            // Build node ID mapping by matching order
            // DeviceDef.copy creates nodes in the same order as the original
            const oldNodeIds = Object.keys(oldDevice.nodes)
            const newNodeIds = Object.keys(newDevice.nodes)
            oldNodeIds.forEach((oldId, idx) => {
                const newId = newNodeIds[idx]
                nodeIdMap[oldId] = newId
                newNodeById[newId] = newDevice.nodes[newId]
            })

            session.devices[newDevice.id] = newDevice
        }

        // Copy chains if requested
        if (importChains && template.chains.length > 0) {
            session.chains = template.chains.map(templateChain => {
                const chain = Chain.copy(templateChain, Identity.create)

                // Preserve active state from template, clear other transient state
                const active = templateChain.cond?.active ?? true
                chain.cond = { active }
                chain.state.midi = undefined

                // Remap module references in all chain items
                // If remapping fails, remove the module to avoid stale references
                chain.lanes.forEach(lane => {
                    lane.cells.forEach(cell => {
                        cell.items.forEach(item => {
                            if (item.L) {
                                const newModule = SuperModule.copy({
                                    oldModule: item.L,
                                    nodeIdMap,
                                    newNodeById,
                                    arch
                                })
                                item.L = newModule
                            }
                            if (item.R) {
                                const newModule = SuperModule.copy({
                                    oldModule: item.R,
                                    nodeIdMap,
                                    newNodeById,
                                    arch
                                })
                                item.R = newModule
                            }
                        })
                    })
                })

                return chain
            })
        }

        return session
    }
}